export type MissingItem = {
  label: string;
  message: string;
  weight: number;
  tabTarget: string | null;
  fieldRef: string;
};

export function computeProfileStrength(fields: {
  isLinked: boolean;
  gpa: string;
  highlights: string[];
  satScore: string;
  preferredMajors: string[];
  swingVideoUrl: string;
  displayName: string;
  cellNumber: string;
  personalStatement: string;
  otherSports: string[];
  swingSpeed: string;
}): { percentage: number; missingItems: MissingItem[] } {
  const definitions: (MissingItem & { isFilled: boolean })[] = [
    {
      label: "JGS Profile",
      message: "Link your JGS profile to unlock school matching",
      weight: 20,
      tabTarget: null,
      fieldRef: "jgs",
      isFilled: fields.isLinked,
    },
    {
      label: "GPA",
      message: "Add GPA to enable academic fit scoring",
      weight: 10,
      tabTarget: "academics",
      fieldRef: "gpa",
      isFilled: fields.gpa.trim() !== "",
    },
    {
      label: "Highlights",
      message: "Add highlights to strengthen coach emails",
      weight: 10,
      tabTarget: "golf",
      fieldRef: "highlights",
      isFilled: fields.highlights.length > 0,
    },
    {
      label: "SAT Score",
      message: "Add SAT score to check admissions fit",
      weight: 10,
      tabTarget: "academics",
      fieldRef: "sat",
      isFilled: fields.satScore.trim() !== "",
    },
    {
      label: "Preferred Majors",
      message: "Add preferred majors to check program availability",
      weight: 10,
      tabTarget: "academics",
      fieldRef: "majors",
      isFilled: fields.preferredMajors.length > 0,
    },
    {
      label: "Swing Video",
      message: "Add a swing video — coaches want to see your swing",
      weight: 10,
      tabTarget: "golf",
      fieldRef: "swingVideo",
      isFilled: fields.swingVideoUrl.trim() !== "",
    },
    {
      label: "Player Name",
      message: "Add your name for personalized communications",
      weight: 5,
      tabTarget: "account",
      fieldRef: "name",
      isFilled: fields.displayName.trim() !== "",
    },
    {
      label: "Cell Number",
      message: "Add your cell number so coaches can reach you",
      weight: 5,
      tabTarget: "account",
      fieldRef: "cell",
      isFilled: fields.cellNumber.trim() !== "",
    },
    {
      label: "Why I Play Golf",
      message: "Give AI a real personal angle for chats, emails, and resumes",
      weight: 10,
      tabTarget: "whole-player",
      fieldRef: "personalStatement",
      isFilled: fields.personalStatement.trim() !== "",
    },
    {
      label: "Other Sports",
      message: "Show team experience and broader athleticism",
      weight: 5,
      tabTarget: "whole-player",
      fieldRef: "otherSports",
      isFilled: fields.otherSports.length > 0,
    },
    {
      label: "Swing Speed",
      message: "Add extra athletic context that helps AI write stronger outreach",
      weight: 10,
      tabTarget: "whole-player",
      fieldRef: "swingSpeed",
      isFilled: fields.swingSpeed.trim() !== "",
    },
  ];

  const filledWeight = definitions.filter(d => d.isFilled).reduce((sum, d) => sum + d.weight, 0);

  const missingItems = definitions
    .filter(d => !d.isFilled)
    .sort((a, b) => b.weight - a.weight)
    .map(({ isFilled: _, ...item }) => item);

  return { percentage: filledWeight, missingItems };
}

export function filterDismissed(items: MissingItem[], dismissed: string[]): MissingItem[] {
  return items.filter(item => !dismissed.includes(item.fieldRef));
}
